import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import api from '../services/api';
import { formatMonto, formatFecha } from '../utils/format';

// ============================================================
// Precios por proveedor (#100), dentro del panel de costo de un producto:
// una fila por proveedor (último precio, mínimo, máximo, compras) ordenada
// del más barato al más caro, y el historial completo plegable. Los montos
// van en la vista `tasa` (BCV | USD) con las tasas del día de cada compra.
// ============================================================

const pref = (tasa) => (tasa === 'BCV' ? 'BCV$ ' : '$');
const costo = (v, tasa) => (v == null ? 'sin tasa' : `${pref(tasa)}${formatMonto(v, 6)}`);

// En la pantalla Comparador de precios se usa con `pantalla`: sin el título
// de sección y con el historial ya desplegado.
export default function PreciosPorProveedor({ productoId, tasa, unidad, pantalla = false }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [verHistorial, setVerHistorial] = useState(pantalla);

  useEffect(() => {
    setDatos(null);
    setError('');
    api.get('/producto-costo-historial/por-proveedor', { params: { producto_id: productoId, tasa } })
      .then(({ data }) => setDatos(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudieron cargar los precios por proveedor'));
  }, [productoId, tasa]);

  if (error) return <p className="text-red-600 text-xs">{error}</p>;
  if (!datos) return <p className="text-xs text-slate-400">Cargando precios por proveedor…</p>;

  const { proveedores, historial } = datos;
  const masBarato = proveedores.find((p) => p.ultimo != null);

  return (
    <div className={pantalla ? 'space-y-3' : 'border-t border-slate-200 pt-2 space-y-2'}>
      {!pantalla && (
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          Precios por proveedor {unidad && <span className="normal-case font-normal">· por {unidad}</span>}
        </p>
      )}

      {proveedores.length === 0 ? (
        <p className="text-sm text-slate-400">Todavía no hay compras de este producto con proveedor.</p>
      ) : (
        <>
          <div className={`bg-white overflow-x-auto ${pantalla ? 'rounded-xl shadow-sm' : 'rounded-lg'}`}>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="p-2">Proveedor</th>
                  <th className="p-2 text-right whitespace-nowrap">Último precio</th>
                  <th className="p-2 whitespace-nowrap">Fecha</th>
                  <th className="p-2 text-right">Mínimo</th>
                  <th className="p-2 text-right">Máximo</th>
                  <th className="p-2 text-right">Compras</th>
                </tr>
              </thead>
              <tbody>
                {proveedores.map((p) => {
                  const esMejor = masBarato && p.proveedor_id === masBarato.proveedor_id && proveedores.length > 1;
                  const dif = masBarato && p.ultimo != null && masBarato.ultimo > 0 && !esMejor
                    ? ((p.ultimo - masBarato.ultimo) / masBarato.ultimo) * 100
                    : null;
                  return (
                    <tr key={p.proveedor_id} className={`border-t ${esMejor ? 'bg-emerald-50' : ''}`}>
                      <td className="p-2 font-medium">
                        {p.proveedor_nombre}
                        {esMejor && <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 align-middle">Más barato</span>}
                      </td>
                      <td className={`p-2 text-right whitespace-nowrap tabular-nums ${esMejor ? 'text-emerald-700 font-semibold' : ''}`}>
                        {costo(p.ultimo, tasa)}
                        {dif != null && dif > 0 && <span className="ml-1 text-xs text-red-600">+{formatMonto(dif, 1)}%</span>}
                      </td>
                      <td className="p-2 whitespace-nowrap text-slate-500">{formatFecha(p.fecha_ultimo)}</td>
                      <td className="p-2 text-right whitespace-nowrap tabular-nums text-slate-500">{costo(p.minimo, tasa)}</td>
                      <td className="p-2 text-right whitespace-nowrap tabular-nums text-slate-500">{costo(p.maximo, tasa)}</td>
                      <td className="p-2 text-right text-slate-500">{p.compras}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button
            onClick={() => setVerHistorial((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-primary-600"
          >
            {verHistorial ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            Historial de compras ({historial.length})
          </button>

          {verHistorial && (
            <div className={`bg-white overflow-x-auto overflow-y-auto ${pantalla ? 'rounded-xl shadow-sm max-h-[32rem]' : 'rounded-lg max-h-72'}`}>
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 text-left sticky top-0">
                  <tr>
                    <th className="p-2">Fecha</th>
                    <th className="p-2">Proveedor</th>
                    <th className="p-2">Factura</th>
                    <th className="p-2 text-right">Precio original</th>
                    <th className="p-2 text-right">En {tasa}</th>
                  </tr>
                </thead>
                <tbody>
                  {historial.map((h) => (
                    <tr key={h.id} className="border-t">
                      <td className="p-2 whitespace-nowrap">{formatFecha(h.fecha)}</td>
                      <td className="p-2">{h.proveedor_nombre}</td>
                      <td className="p-2 whitespace-nowrap text-slate-500">{h.factura_numero ? `#${h.factura_numero}` : 'manual'}</td>
                      <td className="p-2 text-right whitespace-nowrap tabular-nums">
                        {h.moneda === 'BS' ? 'Bs ' : '$'}{formatMonto(h.costo_unitario, 6)}
                      </td>
                      <td className="p-2 text-right whitespace-nowrap tabular-nums">{costo(h.costo_vista, tasa)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
